import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { verifyMagicLink } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { LangToggle, useT } from '../i18n';
import { describeApiError } from '../lib/apiErrors';

export function VerifyPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { authenticate } = useAuth();
  const t = useT();
  // The error is stored as a message KEY (or a raw server message) so a
  // language switch after the failure re-renders it in the new language.
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError(t('verify.missingToken'));
      return;
    }
    void (async () => {
      try {
        const { jwt, partner } = await verifyMagicLink(token);
        authenticate(jwt, partner);
        navigate('/dashboard', { replace: true });
      } catch (e) {
        setError(describeApiError(e, t));
      }
    })();
    // `t` is deliberately omitted: re-running the verify on a language switch
    // would re-consume the one-shot token.
  }, [params, authenticate, navigate]);

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      {/* Same standalone toggle as Login — this page mounts outside Layout. */}
      <div className="flex justify-end mb-4">
        <LangToggle />
      </div>
      {error ? (
        <div className="card p-6">
          <h1 className="text-lg font-semibold text-slate-900">{t('verify.failedTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="btn-primary mt-4"
          >
            {t('verify.newLink')}
          </button>
        </div>
      ) : (
        <div className="grid place-items-center min-h-[40vh] text-slate-500">
          {t('verify.inProgress')}
        </div>
      )}
    </div>
  );
}
